import { DESCRIPTION_COPY_LIMITS, TITLE_COPY_LIMITS } from "./copy-policy";
import { validateScalarSlotLength } from "./scalar-slot-validation";
/**
 * The ONE size a person's portrait is framed to.
 *
 * The card draws every portrait in the same 284x269 frame at every width — there
 * is no second breakpoint and no per-screen variant — so this is that box at 2x.
 * Declared here and consumed by `personGridSchema`, so the size the picker
 * enforces and the size a stored reference is named by cannot drift.
 *
 * ONE target, and it must stay one. A repeat item's media is rendered through a
 * bare resolve of its stored reference; the resolver that derives a second
 * framing from a slot's declaration is block-level and is never applied to an
 * item. A second target here would be declared, framed, and never rendered.
 *
 * `accepts` is deliberately unset, which reads as pictures only. A repeat item's
 * stored reference carries no media type — nothing writes one and nothing reads
 * one back — so a slot that accepted video would offer the Editor a video and
 * then lose the fact that it was one. An avatar is a picture.
 */
export const PERSON_PORTRAIT_SLOT = {
    key: "portrait",
    label: "Portrait",
    targets: [{ width: 568, height: 538, aspectRatio: "284:269" }],
};
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Does this part say anything at all? */
function saysNothing(value) {
    return typeof value !== "string" || value.trim() === "";
}
/** The inverse, for the optional parts where presence is what matters. */
function saysSomething(value) {
    return !saysNothing(value);
}
/** Validate a whole Person Grid. */
export function validatePersonGrid(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The Person Grid is malformed" };
    }
    const { heading, sectionOneTitle, sectionTwoTitle, sectionOnePeople, sectionTwoPeople, } = value;
    if (saysNothing(heading)) {
        return { valid: false, reason: "A person grid needs a heading" };
    }
    const titleLength = validateScalarSlotLength({ ...TITLE_COPY_LIMITS, label: "Title" }, heading);
    if (!titleLength.valid)
        return titleLength;
    if (typeof value.intro === "string") {
        const descriptionLength = validateScalarSlotLength({ ...DESCRIPTION_COPY_LIMITS, label: "Description" }, value.intro);
        if (!descriptionLength.valid)
            return descriptionLength;
    }
    if (typeof sectionOneTitle === "string") {
        const sectionOneTitleLength = validateScalarSlotLength({ ...TITLE_COPY_LIMITS, label: "Section 1 title" }, sectionOneTitle);
        if (!sectionOneTitleLength.valid)
            return sectionOneTitleLength;
    }
    if (typeof sectionTwoTitle === "string") {
        const sectionTwoTitleLength = validateScalarSlotLength({ ...TITLE_COPY_LIMITS, label: "Section 2 title" }, sectionTwoTitle);
        if (!sectionTwoTitleLength.valid)
            return sectionTwoTitleLength;
    }
    if (!Array.isArray(sectionOnePeople) || !Array.isArray(sectionTwoPeople)) {
        return { valid: false, reason: "The Person Grid is malformed" };
    }
    if (sectionOnePeople.length === 0) {
        return {
            valid: false,
            reason: "The first section needs at least one person",
        };
    }
    // A section is a name and the people in it, or neither. Both halves of the
    // rule are one message because they are one rule reached from opposite sides.
    const secondIsNamed = saysSomething(sectionTwoTitle);
    const secondIsPeopled = sectionTwoPeople.length > 0;
    if (secondIsNamed !== secondIsPeopled) {
        return {
            valid: false,
            reason: "A second section is a name and the people in it, or neither",
        };
    }
    // Only once a second section exists does the first one's name get rendered —
    // it becomes half of the toggle, and a toggle with a nameless half cannot be
    // used. With one section it is never shown, so it is never required.
    if (secondIsPeopled && saysNothing(sectionOneTitle)) {
        return {
            valid: false,
            reason: "The first section needs a name once there is a second",
        };
    }
    for (const people of [sectionOnePeople, sectionTwoPeople]) {
        for (const person of people) {
            if (!isRecord(person)) {
                return { valid: false, reason: "The Person Grid is malformed" };
            }
            if (typeof person.bio === "string") {
                const length = validateScalarSlotLength({ ...DESCRIPTION_COPY_LIMITS, label: "Bio" }, person.bio);
                if (!length.valid)
                    return length;
            }
            if (saysNothing(person.name)) {
                return { valid: false, reason: "Every person needs a name" };
            }
        }
    }
    return {
        valid: true,
        personGrid: value,
    };
}
/** New sections require a title; older saved directories remain publishable. */
export function collectPersonGridPublishIssues(block) {
    const rows = block.repeats?.sections ?? [];
    return rows.flatMap((row, index) => row.scalars?.label?.trim()
        ? []
        : [
            {
                message: `Person section ${index + 1} needs a title.`,
                fieldKind: "scalar",
                fieldKey: "label",
                repeatKey: "sections",
                itemId: row.id ?? row.itemId,
            },
        ]);
}
export function validatePersonGridBlock(block) {
    const [issue] = collectPersonGridPublishIssues(block);
    return issue ? { valid: false, reason: issue.message } : { valid: true };
}
//# sourceMappingURL=person-grid.js.map